import React, { useEffect, useMemo, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatRut, formatVat } from "../../../../../utils/formatter";
import {
  CreateAgenda,
  UpdateAgenda,
  AgendaByRut,
  FindAgenda,
  Delete_Agenda,
} from "../../../../../graphql/agenda";
import { ComunasListSemantic } from "../../../../../utils/comunas";
import { importAlertState } from "../../../../../redux/actions/alert";
import { importUpdateNominaData } from 'src/redux/actions/proveedores'
import SelectorEERR from "../selectorEERR";
import BankSelection from "../../formTypes/bankSelection";
import { Form, Button, Input, Dropdown, Icon } from "semantic-ui-react";
import FilterTabs from "src/components/FilterTabs/FilterTabs";
import { importOpenSidebar } from "../../../../../redux/actions/openSidebar";
import { importUpdateNeeded, importUpdateCashflowNeeded } from "../../../../../redux/actions/flujocaja";
import "../../../../../scss/_semanticForms.scss";
import CashflowAreaDropdown from "../cashflowAreaDropdown";
import { PaymentDaysDropdown } from "../paymentDaysDropdown/paymentDaysDropdown";
import "./newAgenda.scss"
import { FlowLVL } from "src/utils/flujoCaja";
import { worldWide } from 'src/utils/worldWideEnum'
import { nominaLabel } from "src/utils/newNominaLabel";
import { FULL_ACCESS_PLANS, Plans } from "src/utils/plans";
import { GetCostCenter } from "src/graphql/costCenter";
import CostCentersInput from "src/components/shared/centroDeCostos/CostCenterInput";
import { CompanyContext } from "src/context";
import { GetProviderLabels } from "src/graphql/labels";
import FeatherIcon from "feather-icons-react";
import { ContactDetail } from "./Components/ContactDetail/ContactDetail";
import { ContactCollection } from "./Components/ContactCollection";
import { ContactParametrization } from "./Components/ContactParametrization";
import { BankDetail } from "./Components/BankDetail";
import { ContactDocument } from "./Components/ContactDocuments";
import { ContactEoafSection } from "./Components/ContactEoafSection/ContactEoafSection";
import SelectorEOAF from "../selectTransactions/selectorEOAF";
import { buildAgendaMutationInput } from "src/utils/agendaMutationInput";

const cleanEmailList = (emails) => {
  if (!Array.isArray(emails)) return [];

  const seen = new Set();

  return emails.reduce((acc, email) => {
    const trimmedEmail = String(email || "").trim();
    if (!trimmedEmail) return acc;

    const normalizedEmail = trimmedEmail.toLowerCase();
    if (seen.has(normalizedEmail)) return acc;

    seen.add(normalizedEmail);
    acc.push(trimmedEmail);
    return acc;
  }, []);
};

const getInitialActiveTab = (showBankTab, showCobranzaTab) => {
  if (showBankTab) return "datos-bancarios";
  if (showCobranzaTab) return "cobranza";
  return "detalles";
};

export default function NewAgenda() {
  const { t, i18n } = useTranslation("agenda");
  const dispatch = useDispatch();
  const [variables, setVariables] = useState({
    isClient: false,
    isProvider: false,
    paymentDaysClient: 30,
    paymentDaysProvider: 30,
    worldWide: 'cl'
  });
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [formattedRut, setFormattedRut] = useState();
  const [selectedCostCentersIds, setSelectedCostCentersIds] = useState([]);

  const { roles } = useSelector((state) => state.auth)

  const {
    selectedCompany: { plan, id: companyId },
  } = useSelector((state) => state.companyList);
  const {
    openSidebar: { editData, nomina, showBankTab, showCobranzaTab },
  } = useSelector((state) => state.openSidebar);
  const { executeMutation: createAgenda } = CreateAgenda();
  const { executeMutation: updateAgenda } = UpdateAgenda();
  const { data, reexecuteQuery } = FindAgenda(editData?.id)

  const resolvedAgenda = useMemo(() => {
    if (data?.Agenda?.id && editData?.id && data.Agenda.id === editData.id) {
      return data.Agenda;
    }
    if (editData?.id) {
      return {
        id: editData.id,
        bankAccounts: editData.bankAccounts ?? [],
      };
    }
    return undefined;
  }, [data?.Agenda, editData]);

  const companyPlans = plan || [];

  const getCostCenter = GetCostCenter(companyId);
  const { data: costCenterData } = getCostCenter;


  const { selectedCompany } = useContext(CompanyContext)

  const showEerr = (selectedCompany && selectedCompany?.plan
    && (selectedCompany?.plan.includes(Plans.smartcfo))
    && selectedCompany?.plan.length == 1);


  const { data: getCompanyLabelsData, error: getCompanyLabelsError } = GetProviderLabels({ companyId });
  const [companyLabels, setCompanyLabels] = useState([]);
  const { executeMutation: deleteAgenda } = Delete_Agenda();

  const labelIcon = companyLabels?.find(
    (f) => f.key === variables?.nominasLabel
  );

  const bankAccountTypes = [
    { key: "corriente", text: "Corriente", value: "corriente" },
    { key: "vista", text: "Vista", value: "vista" },
    { key: "ahorro", text: "Ahorro", value: "ahorro" },
    { key: "chequera", text: "Chequera", value: "chequera" },
  ]

  const [activeTab, setActiveTab] = useState(
    getInitialActiveTab(showBankTab, showCobranzaTab)
  );

  useEffect(() => {
    if (editData) {
      setActiveTab(getInitialActiveTab(showBankTab, showCobranzaTab));
    }
  }, [editData?.id, showBankTab, showCobranzaTab]);

  const editTabs = useMemo(
    () => [
      { key: "detalles", value: "detalles", label: t("editTabs.details") },
      { key: "parametrizacion", value: "parametrizacion", label: t("editTabs.parametrization") },
      { key: "datos-bancarios", value: "datos-bancarios", label: t("editTabs.bankDetails") },
      { key: "documentos", value: "documentos", label: t("editTabs.documents") },
      { key: "cobranza", value: "cobranza", label: t("editTabs.collection") },
    ],
    [t, i18n.language]
  );

  useEffect(() => {
    if (!getCompanyLabelsData?.getProviderLabels || getCompanyLabelsError) return

    const labels = getCompanyLabelsData?.getProviderLabels
    setCompanyLabels(labels);
  }, [getCompanyLabelsData]);

  useEffect(() => {
    if (variables.worldWide && variables.rut) {
      const rut = variables.worldWide !== 'other' ? formatRut(variables.rut) : formatVat(variables.rut);
      setFormattedRut(rut)
    }
  }, [variables.worldWide, variables.rut])

  // Refetch when selected contact changes or cached agenda does not match selection
  useEffect(() => {
    if (!editData?.id) return;
    if (data?.Agenda?.id !== editData.id) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  }, [editData?.id, data?.Agenda?.id, reexecuteQuery]);

  useEffect(async () => {
    if (editData) {
      delete editData.updatedAt;
      delete editData.__typename;

      const updatedData = { ...variables, ...editData };
      delete updatedData.id;

      setVariables(updatedData);
    }
    if (data?.Agenda?.id && editData?.id && data.Agenda.id === editData.id) {
      let parsedObject = {};
      Object.keys(data.Agenda).forEach((el) => {
        if (el !== "id" && el !== "__typename" && el !== "updatedAt") {
          parsedObject = { ...parsedObject, [el]: data.Agenda?.[el] };
        }
      });

      setVariables(parsedObject);
    }
  }, [editData, data]);

  const onChange = (route, value) => {
    setVariables({ ...variables, ...{ [route]: value ?? null } });
  };

  const onChangeCostCenter = (selectedIds, route) => {
    if (selectedIds.length > 0) {
      setVariables({ ...variables, ...{ [route]: selectedIds[0] } });
    } else {
      setVariables({ ...variables, ...{ [route]: [] } });
    }
  }

  const onSubmit = async (variableOverrides = {}) => {
    setLoading(true);

    dispatch(
      importAlertState({
        content: t("alerts.creatingContact"),
        state: "loading",
        duration: 4,
      })
    );

    const agendaId = data?.Agenda?.id ?? editData?.id;
    const agendaMutation = agendaId ? updateAgenda : createAgenda;
    let payload = {};

    const nextVariables = { ...variables, ...variableOverrides };

    if (nextVariables.isClient && !nextVariables.isProvider) {
      delete nextVariables.bankId;
      delete nextVariables.bankAccountType;
      delete nextVariables.bankRut;
      delete nextVariables.bankAccount;
      nextVariables.defaultEERRAreaProvider = null;
    }

    if (!nextVariables.isClient && nextVariables.isProvider) {
      delete nextVariables.zone;
      delete nextVariables.address;
      delete nextVariables.activity;
      nextVariables.defaultEERRAreaClient = null;
    }

    // Filtrar emails vacios y duplicados antes de guardar
    const cleanedVariables = { ...nextVariables };
    cleanedVariables.secondaryEmails = cleanEmailList(cleanedVariables.secondaryEmails);
    cleanedVariables.collectionEmails = cleanEmailList(cleanedVariables.collectionEmails);
    if (Array.isArray(cleanedVariables.collectionEmailContacts)) {
      cleanedVariables.collectionEmailContacts = cleanedVariables.collectionEmailContacts
        .map((c) => ({
          email: String(c?.email || "").trim(),
          contactName: String(c?.contactName || "").trim(),
        }))
        .filter((c) => c.email);
      cleanedVariables.collectionEmails = cleanedVariables.collectionEmailContacts.map((c) => c.email);
    }

    const mutationInput = buildAgendaMutationInput(cleanedVariables);

    if (!agendaId) payload = { input: { ...mutationInput, companyId } };
    else payload = { id: agendaId, input: mutationInput };

    const { error, data: createAgendaData } = await agendaMutation(payload);

    if (error) {
      setLoading(false);

      return dispatch(
        importAlertState({
          content: t("alerts.unexpectedError"),
          state: "error",
          duration: 5,
        })
      );
    }

    if (createAgendaData?.createAgenda?.status === false) {
      setLoading(false);

      return dispatch(
        importAlertState({
          content: createAgendaData?.createAgenda?.message,
          state: "error",
          duration: 5,
        })
      );
    }

    // Get the new agenda ID after creation
    const newAgendaId = createAgendaData?.createAgenda?.agenda?.id;
    
    // If we created a new agenda and have showBankTab, keep sidebar open and update editData
    if (!data && newAgendaId && showBankTab) {
      // Update editData with the new ID
      const updatedEditData = { ...editData, id: newAgendaId };
      
      // Update sidebar state with new editData
      dispatch(
        importOpenSidebar({
          show: true,
          newAgenda: false, // Change to false since agenda now exists
          editData: updatedEditData,
          nomina: nomina,
          showBankTab: showBankTab,
        })
      );
      
      // Switch to bank tab
      setActiveTab("datos-bancarios");
      
      setLoading(false);
      
      dispatch(importUpdateNominaData(true))
      dispatch(importUpdateNeeded({ value: true }));
      dispatch(importUpdateCashflowNeeded({ value: true, reload: true }))

      return dispatch(
        importAlertState({
          content: t("alerts.contactCreatedWithBank"),
          state: "success",
          duration: 4,
        })
      );
    }

    const content = editData
      ? t("alerts.contactUpdated")
      : t("alerts.contactCreated");

    setLoading(false);

    setVariables({});

    dispatch(
      importOpenSidebar({
        show: false,
      })
    );

    dispatch(importUpdateNominaData(true))
    dispatch(importUpdateNeeded({ value: true }));
    dispatch(importUpdateCashflowNeeded({ value: true, reload: true }))

    return dispatch(
      importAlertState({
        content,
        state: "success",
        duration: 4,
      })
    );
  };

  const parseRut = (rut) => {
    if (process.env.REACT_APP_COUNTRY == "CHL") {
      if (variables.worldWide === 'cl') {
        return rut.replace(/[^0-9Kk]/g, "")
      }
      return rut.toUpperCase().replace(/[^A-Z0-9]/g, "")
    } else {
      return rut.replace(/\./g, '');
    }
  }

  const disabled =
    !variables.rut ||
    !variables.name ||
    //!variables.email ||
    ![variables.isClient, variables.isProvider].some((f) => f === true) ||
    ![variables.paymentDaysClient, variables.paymentDaysProvider].some(
      (f) => f >= 0
    );
  /*     || variables.isProvider 
      ? !variables.bankAccountType || !variables.bankId || !variables.bankRut || !variables.bankAccount
      : '' */
  //: !variables.address || !variables.zone || !variables.activity

  const onDelete = async (id) => {
    setLoadingDelete(true);
    const { error } = await deleteAgenda({ id });

    if (error) {
      setLoadingDelete(false);
      return dispatch(
        importAlertState({
          content: t("alerts.deleteError"),
          state: "error",
          duration: 5,
        })
      );
    }

    setLoadingDelete(false);

    dispatch(
      importOpenSidebar({
        show: false,
      })
    );

    return dispatch(
      importAlertState({
        content: t("alerts.contactDeleted"),
        state: "success",
        duration: 5,
      })
    );
  };

  return (
    <>
      {
        editData ?
          <div className="modal-container">
            <FilterTabs
              tabs={editTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="tw-mb-3"
            />
            <div className="tw-pt-3">
              {activeTab === "detalles" && (
                <ContactDetail
                  variables={variables}
                  loading={loading}
                  loadingDelete={loadingDelete}
                  onChange={onChange}
                  onSubmit={onSubmit}
                  onDelete={onDelete}
                  contactId={editData?.id}
                />
              )}
              {activeTab === "parametrizacion" && (
                <div className="contact-parametrization parametrizacion-tab-content">
                  <ContactParametrization
                    variables={variables}
                    companyPlans={companyPlans}
                    companyId={companyId}
                    loading={loading}
                    labelIcon={labelIcon}
                    companyLabels={companyLabels}
                    onChange={onChange}
                    onSubmit={onSubmit}
                  />
                  <ContactEoafSection
                    companyId={companyId}
                    contactId={editData?.id}
                    contactName={variables?.name || resolvedAgenda?.name || ''}
                  />
                </div>
              )}
              {activeTab === "datos-bancarios" && (
                <BankDetail
                  key={editData?.id}
                  agenda={resolvedAgenda}
                  bankAccountTypes={bankAccountTypes}
                  agendaId={editData?.id}
                  reexecuteQueryAgenda={reexecuteQuery}
                />
              )}
              {activeTab === "documentos" && (
                <ContactDocument
                  agendaId={editData?.id}
                  companyId={companyId}
                />
              )}
              {activeTab === "cobranza" && (
                <ContactCollection
                  variables={variables}
                  loading={loading}
                  onChange={onChange}
                  onSubmit={onSubmit}
                />
              )}
            </div>
          </div>
          :
          <div className="d-flex flex-column w-100">
            <div className="formInputHeader mb-4">{t("initialData")}</div>
            <Form widths="equal">
              <div className="d-flex">
                <Form.Field
                  label={variables.worldWide !== 'other' ? t("rutLabel") : t("idLabel")}
                  control={Input}
                  placeholder={variables.worldWide !== 'other' ? t("rutPlaceholder") : t("idPlaceholder")}
                  value={formattedRut}
                  onChange={(_, v) => onChange("rut", parseRut(v.value))}
                  maxLength={process.env.REACT_APP_COUNTRY == "CHL" ? 12 : 30}
                  required
                  className="w-100"
                  fluid
                />
                <Form.Dropdown
                  selectOnBlur={false}
                  options={worldWide}
                  basic
                  button
                  fluid
                  className="ml-3 worldWideWidth"
                  label={t("typeLabel")}
                  onChange={(_, v) => onChange("worldWide", v.value)}
                  value={variables.worldWide || 'cl'}
                />
              </div>
              <Form.Field
                label={t("nameLabel")}
                control={Input}
                placeholder={t("namePlaceholder")}
                value={variables.name}
                onChange={(_, v) => onChange("name", v.value)}
                required
                className="w-100"
                fluid
              />
              <Form.Field
                label={t("emailLabel")}
                type="email"
                control={Input}
                placeholder={t("emailPlaceholder")}
                value={variables.email}
                onChange={(_, v) => onChange("email", v.value)}
                className="w-100"
                fluid
              />

        { ['677d45356d129a001c2d0cbe'].includes(companyId) && <Form.Field
          label="ETIQUETA"
          type="labels"
          control={Input}
          placeholder="Ingrese etiqueta"
          value={variables.labels?.length > 0 ? variables.labels[0] : ''}
          onChange={(_, v) => onChange("labels", [v.value])}
          className="w-100"
          fluid
        /> }

              <Form.Field className="d-flex justify-content-around m-0 w-100" fluid>
                <Form.Checkbox
                  label={t("client")}
                  checked={variables.isClient}
                  value={variables.isClient}
                  onChange={(_, v) => onChange("isClient", v.checked)}
                />
                <Form.Checkbox
                  label={t("provider")}
                  checked={variables.isProvider}
                  value={variables.isProvider}
                  onChange={(_, v) => onChange("isProvider", v.checked)}
                />
              </Form.Field>

              <div
                className="formInputHeader w-100 mb-2"
                hidden={companyPlans.includes(Plans.flujo_de_caja) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                style={{
                  display: !variables.isClient && !variables.isProvider ? "none" : "",
                }}
              >
                Flujo de Caja
              </div>
              <Form.Group
                className="w-100"
                // hidden={companyPlans.includes(Plans.flujo_de_caja) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                style={{ display: variables.isClient ? "" : "none" }}
                inline
              >
                <Form.Field
                  className="mb-1 mr-2"
                  hidden={companyPlans.includes(Plans.flujo_de_caja) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                >
                  <label>ÁREA FLUJO DE CAJA (CLIENTE)</label>
                  <CashflowAreaDropdown
                    editData={{ cashflowAreaId: variables.defaultCashflowAreaClient }}
                    onChange={v => onChange("defaultCashflowAreaClient", v)}
                    limit={[FlowLVL.INCOME]}
                  />
                </Form.Field>

                <Form.Field
                  className="mb-1 paymentDaysComponent"
                  required={variables.isClient}
                >
                  <label>A CUÁNTOS DÍAS (CLIENTE)</label>
                  <PaymentDaysDropdown
                    editData={variables.paymentDaysClient}
                    onChange={v => onChange("paymentDaysClient", parseFloat(v))}
                  />
                </Form.Field>
              </Form.Group>

              <Form.Group
                className="w-100"
                style={{ display: variables.isProvider ? "" : "none" }}
                inline
              >
                <Form.Field
                  className="mb-1 mr-2"
                  hidden={companyPlans.includes(Plans.flujo_de_caja) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                >
                  <label>ÁREA FLUJO DE CAJA (PROVEEDOR)</label>
                  <CashflowAreaDropdown
                    editData={{ cashflowAreaId: variables.defaultCashflowAreaProvider }}
                    onChange={v => onChange("defaultCashflowAreaProvider", v)}
                    limit={[FlowLVL.EXPENSE]}
                  />
                </Form.Field>

                <Form.Field
                  className="mb-1 paymentDaysComponent"
                  required={variables.isProvider}
                >
                  <label>A CUÁNTOS DÍAS (PROVEEDOR)</label>
                  <PaymentDaysDropdown
                    editData={variables.paymentDaysProvider}
                    onChange={v => onChange("paymentDaysProvider", parseFloat(v))}
                  />
                </Form.Field>
              </Form.Group>

              {/* <div
          className="formInputHeader w-100 mb-2"
          hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
          style={{
            display: !variables.isClient && !variables.isProvider ? "none" : "",
          }}
        >
          Estado de Origen y Aplicación de Fondos (EOAF)
        </div>
        <Form.Group
          hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
          style={{ display: variables.isClient ? "" : "none" }}
        >
          <Form.Field
            label="EOAF (CLIENTE)"
            control={() => (
              <SelectorEOAF
                value={variables.defaultEOAFAreaClient}
                onChange={(_, v) => onChange("defaultEOAFAreaClient", v?.value)}
                placeholder="Área Cliente"
                companyId={companyId}
              />
            )}
            // onClear={() => onChange("defaultEOAF", "")}
            fluid
          />
        </Form.Group>
        <Form.Group
          hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
          style={{ display: variables.isProvider ? "" : "none" }}
        >
          <Form.Field
            label="EOAF (PROVEEDOR)"
            control={() => (
              <SelectorEOAF
                value={variables.defaultEOAFAreaProvider}
                onChange={(_, v) => onChange("defaultEOAFAreaProvider", v?.value)}
                placeholder="Área Proveedor"
                companyId={companyId}
              />
            )}
            //onClear={() => onChange("defaultEERRArea", "")}
            fluid
          />
        </Form.Group> */}
        {/* <div
                className="formInputHeader w-100 mb-2"
                hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                style={{
                  display: !variables.isClient && !variables.isProvider ? "none" : "",
                }}
              >
                Estado de Resultados
              </div>
              <Form.Group
                hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                style={{ display: variables.isClient ? "" : "none" }}
              >
                <Form.Field
                  label="ESTADO DE RESULTADOS (CLIENTE)"
                  control={() => (
                    <SelectorEERR
                      value={variables.defaultEERRAreaClient}
                      onChange={(_, v) => onChange("defaultEERRAreaClient", v?.value)}
                      placeholder="Área Cliente"
                      companyId={companyId}
                    />
                  )}
                  onClear={() => onChange("defaultEERRArea", "")}
                  fluid
                />
              </Form.Group>

              {
                !showEerr &&
                <Form.Group
                  hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                  style={{ display: variables.isClient ? "" : "none" }}
                >
                  <Form.Field
                    label="CENTRO DE COSTOS (CLIENTE)"
                    control={() =>
                      <CostCentersInput
                        data={costCenterData?.getManyCostCenter}
                        value={variables.defaultCostCenterClient}
                        onChangeCostCenter={(selectedIds) => onChangeCostCenter(selectedIds, 'defaultCostCenterClient')}
                        multiple={false}
                      />
                    }
                    fluid
                  />
                </Form.Group>
              }

              <Form.Group
                hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                style={{ display: variables.isProvider ? "" : "none" }}
              >
                <Form.Field
                  label="ESTADO DE RESULTADOS (PROVEEDOR)"
                  control={() => (
                    <SelectorEERR
                      value={variables.defaultEERRAreaProvider}
                      onChange={(_, v) => onChange("defaultEERRAreaProvider", v?.value)}
                      placeholder="Área Proveedor"
                      companyId={companyId}
                    />
                  )}
                  fluid
                />
              </Form.Group>

              {
                !showEerr &&
                <Form.Group
                  hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                  style={{ display: variables.isProvider ? "" : "none" }}
                >
                  <Form.Field
                    label="CENTRO DE COSTOS (PROVEEDOR)"
                    control={() =>
                      <CostCentersInput
                        data={costCenterData?.getManyCostCenter}
                        value={variables.defaultCostCenterProvider}
                        onChangeCostCenter={(selectedIds) => onChangeCostCenter(selectedIds, 'defaultCostCenterProvider')}
                        multiple={false}
                      />
                    }
                    fluid
                  />
                </Form.Group>
              } */}

              {
                !roles.includes('FLUJO_DE_CAJA') &&
                <>
                  <div
                    className="formInputHeader w-100 mb-2"
                    hidden={companyPlans.includes(Plans.pago_a_proveedores) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                    style={{
                      display: !variables.isProvider ? "none" : "",
                    }}
                  >
                    Clasificación de Proveedor
                  </div>
                  <Form.Group
                    style={{ display: variables.isProvider ? "" : "none" }}
                    hidden={companyPlans.includes(Plans.pago_a_proveedores) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
                  >
                    <Form.Field
                      label="CLASIFICACIÓN DEL PROVEEDOR"
                      control={() => (
                        <Dropdown
                          selectOnBlur={false}
                          icon={false}
                          className="d-flex px-3"
                          blue
                          fluid
                          button
                          basic
                          text={
                            labelIcon?.key === "sinetiqueta" || !labelIcon ? (
                              <span className="d-flex align-items-center">
                                <Icon name="add" />{" "}
                                <span className="custom-provider-type-color">
                                  Añadir Etiqueta
                                </span>
                              </span>
                            ) : (
                              <span className="d-flex align-items-center">
                                <FeatherIcon
                                  icon={labelIcon?.icon}
                                  color={labelIcon?.color}
                                  strokeWidth={"2px"}
                                  size={"15px"}
                                  className="tw-mr-3"
                                />{" "}
                                <span className="custom-provider-type-color">
                                  {labelIcon?.name}
                                </span>
                              </span>
                            )
                          }
                        >
                          <Dropdown.Menu>
                            {companyLabels?.map((companyLabel) => (
                              <Dropdown.Item
                                className="d-flex cursor-pointer"
                                value={companyLabel.key}
                                onClick={(_, val) => onChange("nominasLabel", val.value)}
                              >
                                <FeatherIcon
                                  icon={companyLabel.icon}
                                  color={companyLabel.color}
                                  strokeWidth={"2px"}
                                  size={"15px"}
                                  className="tw-mr-2"
                                />
                                <div>{companyLabel.name}</div>
                              </Dropdown.Item>
                            ))}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                      onClear={() => onChange("nominasLabel", "")}
                      fluid
                    />
                  </Form.Group>
                </>
              }

              <div
                className="formInputHeader w-100 mb-2"
                style={{ display: variables.isClient ? "" : "none" }}
              >
                Datos Tributarios
              </div>
              <div
                className="mb-4"
                style={{ display: variables.isClient ? "" : "none" }}
              >
                <Form.Group className="w-100" widths="equal" inline>
                  <Form.Field
                    label="DIRECCIÓN"
                    control={Input}
                    value={variables.address}
                    onChange={(_, v) => onChange("address", v.value)}
                    placeholder="Seleccione Dirección"
                    className="w-100"
                    fluid
                  />
                  <Form.Field
                    label="COMUNA"
                    control={() => (
                      <ComunasListSemantic
                        value={variables.zone}
                        onChange={(_, v) => onChange("zone", v.value)}
                      />
                    )}
                    className="w-100 ml-2"
                    fluid
                  />
                </Form.Group>
                <Form.Field
                  label="GIRO"
                  control={Input}
                  placeholder="Ingrese Giro"
                  value={variables.activity}
                  onChange={(_, v) => onChange("activity", v.value)}
                  className="w-100"
                  fluid
                />
              </div>

              <div
                className="formInputHeader w-100 mb-2"
                style={{ display: variables.isProvider ? "" : "none" }}
              >
                Datos Bancarios
              </div>
              <div style={{ display: variables.isProvider ? "" : "none" }}>
                <Form.Field
                  label="BANCO"
                  control={() => (
                    <BankSelection
                      value={variables.bankId}
                      onChange={(_, v) => onChange("bankId", v.value)}
                    />
                  )}
                  placeholder="Seleccione Banco"
                  style={{ display: variables.isProvider ? "" : "none" }}
                  className="w-100"
                  fluid
                />
                <Form.Group
                  className="w-100"
                  widths="equal"
                  style={{ display: variables.isProvider ? "" : "none" }}
                  inline
                >
                  <Form.Select
                    label="TIPO DE CUENTA"
                    options={bankAccountTypes}
                    value={variables.bankAccountType}
                    onChange={(_, v) => onChange("bankAccountType", v.value)}
                    placeholder="Seleccione Tipo"
                    className="w-100"
                    fluid
                  />
                  <Form.Field
                    label="RUT DESTINATARIO"
                    control={Input}
                    placeholder="Ingrese RUT Destinatario"
                    value={formatRut(variables.bankRut)}
                    onChange={(_, v) =>
                      onChange("bankRut", v.value.replace(/[^0-9Kk]/g, ""))
                    }
                    maxLength={12}
                    className="w-100 ml-2"
                    fluid
                  />
                </Form.Group>
                <Form.Field
                  label="CUENTA BANCARIA"
                  control={Input}
                  placeholder="Ingrese Cuenta Bancaria"
                  value={variables.bankAccount}
                  onChange={(_, v) => onChange("bankAccount", v.value)}
                  className="w-100"
                  fluid
                />
              </div>

              <div className="d-flex justify-content-center mt-4 mb-4 agendaSubmitButton">
                <Button
                  type="button"
                  color="blue"
                  onClick={() => onSubmit()}
                  disabled={disabled}
                  loading={loading}
                >
                  {!data ? t("createContactBtn") : t("saveChanges")}
                </Button>
              </div>
            </Form>
          </div>
      }
    </>
  );
}
